import { useRoom } from "@liveblocks/react";
import { useAtom } from "jotai";
import { isEqual } from "lodash";
import { useCallback, useEffect } from "react";
import { onlinesAtom, presenceAtom, presenceAtomFamily } from "../jotai/atoms";
import { Presence, User } from "../types";

export function useOnlinesSetup() {
  const [presences] = useAtom(presenceAtom);
  const [onlines, setOnlines] = useAtom(onlinesAtom);

  useEffect(() => {
    const uids = new Set(Object.keys(presences));

    if (!isEqual(onlines, uids)) {
      setOnlines(uids);
    }
  }, [presences, onlines, setOnlines]);
}

export function usePresence<T>(
  key: keyof Presence
): [Record<User["id"], T>, (updates: Partial<Presence>) => void] {
  const update = useUpdatePresence();
  const [presences] = useAtom(presenceAtomFamily(key));

  // TODO: figure out how to properly type this
  return [presences, update] as unknown as [
    Record<User["id"], T>,
    (updates: Partial<Presence>) => void
  ];
}

/**
 * useUpdatePresence() returns an presence update function.
 *
 * The hook only triggers re-render on room swtich and hence good for
 * update-only use. (as oppose to using 'usePresence', which
 * triggers re-render on every presence change)
 */
export function useUpdatePresence() {
  const room = useRoom();
  const update = useCallback(
    (updates: Partial<Presence>) => room.updatePresence(updates),
    [room]
  );

  return update;
}

export const DEFAULT_PRESENCE: Presence = {
  position: { x: 0, y: 0 },
  viewport: {
    position: { x: 0, y: 0 },
    size: { width: 0, height: 0 },
  },
  selection: {},
  stampMode: "inactive",
  cursor: "pointer",
  isInAuthFlow: false,
  drawMode: null,
};
